import React, { useEffect, useState } from "react";
import axios from "axios";
import DOMPurify from "dompurify";
import { useNavigate, useParams } from "react-router-dom";
import { ShopHeader } from "@/components/ShopHeader";
import { ShopFooter } from "@/components/ShopFooter";
import { StoreNav } from "@/components/StoreNav";
import { useLang } from "@/context/LanguageContext";
import { CaretRight, ArrowLeft } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BODY_CSS = `
.blog-body ul{list-style:disc;padding-left:1.5rem;margin:0.75rem 0}
.blog-body ol{list-style:decimal;padding-left:1.5rem;margin:0.75rem 0}
.blog-body a{color:#2563eb;text-decoration:underline}
.blog-body p{margin:0.75rem 0;line-height:1.7}
.blog-body h2,.blog-body h3{font-weight:800;color:#2e3a47;margin:1.25rem 0 0.5rem}
.blog-body img{max-width:100%;height:auto;border-radius:0.5rem;margin:1rem 0}
`;

export default function BlogPostPage() {
  const { t, lang } = useLang();
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(undefined);

  useEffect(() => {
    axios.get(`${API}/shop/blog/${slug}`)
      .then((r) => setPost(r.data))
      .catch(() => setPost(null));
  }, [slug]);

  const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString(lang === "fr" ? "fr-CA" : "en-CA", { year: "numeric", month: "long", day: "numeric" }) : "");
  const title = post ? (lang === "en" ? (post.title_en || post.title_fr) : (post.title_fr || post.title_en)) : "";
  const body = post ? (lang === "en" ? (post.body_en || post.body_fr) : (post.body_fr || post.body_en)) : "";

  return (
    <div className="min-h-screen bg-white">
      <ShopHeader />
      <div className="mx-auto max-w-[1280px] px-4 py-4"><StoreNav active="blog" /></div>

      <div className="mx-auto max-w-[820px] px-4 pb-12">
        <style>{BODY_CSS}</style>
        <div className="mb-8 flex items-center gap-1 text-sm text-muted-foreground">
          <button onClick={() => navigate("/")} className="hover:text-primary">{t.home}</button>
          <CaretRight size={12} weight="bold" />
          <button onClick={() => navigate("/dernieres-nouvelles")} className="hover:text-primary">{t.blogHeroTitle}</button>
          <CaretRight size={12} weight="bold" />
          <span className="truncate text-foreground">{title}</span>
        </div>

        {post === undefined ? (
          <div className="py-20 text-center text-muted-foreground">…</div>
        ) : post === null ? (
          <div className="py-20 text-center" data-testid="blog-not-found">
            <p className="mb-6 text-[15px] text-muted-foreground">{t.blogEmpty}</p>
            <button onClick={() => navigate("/dernieres-nouvelles")} className="inline-flex items-center gap-1.5 text-sm font-bold text-primary">
              <ArrowLeft size={15} weight="bold" /> {t.blogBackToList}
            </button>
          </div>
        ) : (
          <article data-testid="blog-post">
            <div className="mb-3 text-xs font-bold uppercase tracking-wide text-primary">{t.blogPublishedOn} {fmtDate(post.published_at)}</div>
            <h1 className="mb-6 font-display text-4xl font-black leading-tight tracking-tight text-[#2e3a47]" data-testid="blog-post-title">{title}</h1>
            {post.cover_image && (
              <img src={post.cover_image} alt={title} className="mb-8 w-full rounded-lg object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />
            )}
            <div className="blog-body text-[16px] text-foreground/90" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(body) }} />
            <div className="mt-12 border-t border-border pt-6">
              <button onClick={() => navigate("/dernieres-nouvelles")} className="inline-flex items-center gap-1.5 text-sm font-bold text-primary hover:underline" data-testid="blog-back">
                <ArrowLeft size={15} weight="bold" /> {t.blogBackToList}
              </button>
            </div>
          </article>
        )}
      </div>

      <ShopFooter />
    </div>
  );
}
